import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '@/presentation/hook/useAuth';
import { useAdminUsers } from '@/presentation/hook/useAdminUsers';
import { Alert, InputField } from '@/presentation/component/ui';
import { UserList } from './UserList';
import { UserDeleteModal } from './UserDeleteModal';
import { UserProfileModal } from './UserProfileModal';
import type { Role } from '@/domain/model/Role';

const DEBOUNCE_MS = 400;

export const AdminUsersPage = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const {
    users,
    userProfile,
    isLoading,
    error,
    fetchUsers,
    fetchUserProfile,
    changeUserRole,
    deleteUser,
    clearUserProfile,
    clearError,
  } = useAdminUsers();

  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchUsers(value || undefined);
      }, DEBOUNCE_MS);
    },
    [fetchUsers],
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleViewProfile = (id: string) => {
    fetchUserProfile(id);
    setIsProfileOpen(true);
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
    clearUserProfile();
  };

  const handleChangeRole = async (id: string, role: Role) => {
    const success = await changeUserRole(id, role);
    if (success) {
      toast.success(t('admin.roleChanged'));
      if (userProfile && userProfile.id === id) {
        fetchUserProfile(id);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (deletingId) {
      const success = await deleteUser(deletingId);
      if (success) {
        toast.success(t('admin.userDeleted'));
      }
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-ink)]">{t('admin.users')}</h1>
          <p className="text-sm text-[var(--color-ink-faint)]">
            {users.length} {t('admin.users').toLowerCase()}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <InputField
          label={t('admin.search')}
          placeholder={t('admin.search')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>

      {error && (
        <Alert kind="bad" onClose={clearError} className="mt-4">
          {t(error)}
        </Alert>
      )}

      {isLoading && !isProfileOpen && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('common.loading')}</p>
      )}

      {!isLoading && users.length === 0 && !error && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('admin.noUsers')}</p>
      )}

      {users.length > 0 && (
        <UserList
          users={users}
          isSuperAdmin={isSuperAdmin}
          onViewProfile={handleViewProfile}
          onDelete={setDeletingId}
        />
      )}

      <UserProfileModal
        open={isProfileOpen}
        profile={userProfile}
        isLoading={isLoading}
        isSuperAdmin={isSuperAdmin}
        onChangeRole={handleChangeRole}
        onClose={handleCloseProfile}
      />

      <UserDeleteModal
        open={deletingId !== null}
        isLoading={isLoading}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeletingId(null)}
      />
    </div>
  );
};
